import { useCallback, useMemo, useState } from 'react';
import { API_BASE } from '../../../config/constants';

const KEY_REMEMBER_EMAIL = 'REMEMBER_ME_EMAIL';
const KEY_REMEMBER_PASSWORD = 'REMEMBER_ME_PASSWORD';
const KEY_DISPLAY_INVITATION_SCREEN = 'DISPLAY_INVITATION_SCREEN';
const KEY_AUTH_TOKEN = 'AUTH_TOKEN';

export type LoginInput = {
    email: string;
    password: string;
    rememberMe: boolean;
};

export type RegisterInput = {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
};

function buildJsonHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    return headers;
}

function getRememberedCredentials() {
    const email = localStorage.getItem(KEY_REMEMBER_EMAIL) ?? '';
    const password = localStorage.getItem(KEY_REMEMBER_PASSWORD) ?? '';
    return {
        email,
        password,
        rememberMe: email.length > 0 && password.length > 0,
    };
}

function toUserCandidate(payload: any): any {
    if (payload?.data?.[0]) return payload.data[0];
    if (payload?.user) return payload.user;
    if (payload?.data && !Array.isArray(payload.data)) return payload.data;
    return null;
}

async function postLoginBody(email: string, password: string) {
    const response = await fetch(`${API_BASE}/api/users/login-user`, {
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify({ email, password, user: { email, password } }),
    });
    const payload = await response.json().catch(() => ({}));
    return { ok: response.ok, payload };
}

async function postLoginQueryFallback(email: string, password: string) {
    const params = new URLSearchParams();
    params.append('user[email]', email);
    params.append('user[password]', password);

    const response = await fetch(`${API_BASE}/api/users/login-user?${params.toString()}`, {
        method: 'POST',
        headers: buildJsonHeaders(),
    });
    const payload = await response.json().catch(() => ({}));
    return { ok: response.ok, payload };
}

async function fetchUserByEmail(email: string) {
    const response = await fetch(`${API_BASE}/api/users/get-user-by-email`, {
        method: 'POST',
        headers: buildJsonHeaders(),
        body: JSON.stringify({ email }),
    });

    if (!response.ok) return null;

    const payload = await response.json().catch(() => null);
    if (!payload || payload.success === false) return null;

    if (Array.isArray(payload.data) && payload.data.length > 0) {
        return payload.data[0];
    }

    return payload.data ?? payload;
}

export function useHLogin() {
    const remembered = useMemo(() => getRememberedCredentials(), []);

    const [email, setEmail] = useState(remembered.email);
    const [password, setPassword] = useState(remembered.password);
    const [rememberMe, setRememberMe] = useState(remembered.rememberMe);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const clearMessages = useCallback(() => {
        setError(null);
        setSuccess(null);
    }, []);

    const login = useCallback(
        async ({ email: inputEmail, password: inputPassword, rememberMe: remember }: LoginInput) => {
            clearMessages();
            setLoading(true);

            try {
                localStorage.removeItem(KEY_AUTH_TOKEN);

                if (remember) {
                    localStorage.setItem(KEY_REMEMBER_EMAIL, inputEmail.trim());
                    localStorage.setItem(KEY_REMEMBER_PASSWORD, inputPassword);
                } else {
                    localStorage.removeItem(KEY_REMEMBER_EMAIL);
                    localStorage.removeItem(KEY_REMEMBER_PASSWORD);
                }

                let result = await postLoginBody(inputEmail.trim(), inputPassword);

                if (!result.ok || !result.payload?.success) {
                    const fallback = await postLoginQueryFallback(inputEmail.trim(), inputPassword);
                    if (fallback.ok && fallback.payload?.success) {
                        result = fallback;
                    }
                }

                if (!result.ok || !result.payload?.success) {
                    throw new Error(result.payload?.message || 'Invalid email or password');
                }

                if (result.payload?.requires2fa && result.payload?.loginToken) {
                    return {
                        success: true,
                        requires2fa: true,
                        loginToken: result.payload.loginToken,
                    };
                }

                localStorage.setItem(KEY_DISPLAY_INVITATION_SCREEN, 'false');

                const candidateFromLogin = toUserCandidate(result.payload);
                const fullUser = await fetchUserByEmail(inputEmail.trim());
                const loggedInUser = {
                    email: inputEmail.trim(),
                    ...(fullUser || {}),
                    ...(candidateFromLogin || {}),
                };

                localStorage.setItem(KEY_AUTH_TOKEN, JSON.stringify(loggedInUser));
                return { success: true, user: loggedInUser };
            } catch (err: any) {
                const message = err?.message || 'Login failed. Please check your credentials.';
                setError(message);
                return { success: false, user: null, message };
            } finally {
                setLoading(false);
            }
        },
        [clearMessages]
    );

    const registerUser = useCallback(
        async (input: RegisterInput) => {
            clearMessages();
            setLoading(true);

            try {
                const response = await fetch(`${API_BASE}/api/users/register-user`, {
                    method: 'POST',
                    headers: buildJsonHeaders(),
                    body: JSON.stringify({
                        user: {
                            email: input.email.trim(),
                            password: input.password,
                            first_name: input.firstName.trim(),
                            last_name: input.lastName.trim(),
                        },
                    }),
                });

                const payload = await response.json().catch(() => ({}));

                if (!response.ok || !payload?.success) {
                    throw new Error(payload?.message || 'Registration failed. Please try again.');
                }

                setSuccess('User registered successfully! You can now log in.');
                return { success: true, payload };
            } catch (err: any) {
                const message = err?.message || 'Registration failed. Please try again.';
                setError(message);
                return { success: false, message };
            } finally {
                setLoading(false);
            }
        },
        [clearMessages]
    );

    const verify2FA = useCallback(
        async (loginToken: string, otpCode: string) => {
            try {
                const response = await fetch(`${API_BASE}/api/users/verify-2fa`, {
                    method: 'POST',
                    headers: buildJsonHeaders(),
                    body: JSON.stringify({ loginToken, otpCode }),
                });

                const payload = await response.json().catch(() => ({}));

                if (!response.ok || !payload?.success) {
                    throw new Error(payload?.message || 'Invalid 2FA code');
                }

                const fullUser = payload.user || toUserCandidate(payload);
                const loggedInUser = fullUser ?? { email };

                localStorage.setItem(KEY_AUTH_TOKEN, JSON.stringify(loggedInUser));
                return { success: true, user: loggedInUser };
            } catch (err: any) {
                const message = err?.message || 'Failed to verify 2FA code';
                setError(message);
                return { success: false, user: null, message };
            }
        },
        [email]
    );

    return {
        email,
        setEmail,
        password,
        setPassword,
        rememberMe,
        setRememberMe,
        loading,
        error,
        success,
        setError,
        setSuccess,
        clearMessages,
        login,
        registerUser,
        verify2FA,
    };
}
